/**
 * The Dispatcher frame: the Operator Admin's dark bar, with the brand, the
 * one section there is (the board) and who is signed in, then the screen
 * itself (`<Outlet />`).
 *
 * The same `.pp-op` chrome as OperatorLayout on purpose — it is the same
 * brand, the same shell, the same tokens — with a "Pickup Hungary" badge in
 * place of an operator's name, because this login belongs to no operator.
 * There is no second tab: a dispatcher has one screen, and a trip's details
 * open from it.
 *
 * Rendered inside PortalRoute → PortalShell, so the portal's tokens, fonts,
 * keep-alive and guard all apply. Its route asks the shell to be `wide`
 * (PortalRoutes.jsx), so it gets a desktop layout; the board itself is
 * phone-first and collapses to cards.
 *
 * The frame owns two things besides the bar, and for the same reason: both
 * have to be reachable from EVERY dispatcher screen, so neither can live on
 * the board or on a trip.
 *
 *   "this device" (DispatcherDevice)   the Home Screen offer and
 *                                      notifications for the phone this is
 *                                      open on — and silent on a desktop,
 *                                      which is how a frame should treat
 *                                      something a desk cannot use.
 *   the assistant (DispatcherAssistant) the chat agent, in the portal's own
 *                                      sheet. Held HERE rather than inside
 *                                      the sheet so closing it to look at the
 *                                      board does not abort the answer.
 *
 * "this device" is one 44px icon in the bar's existing cluster. The assistant
 * is a floating launcher at the bottom right instead — the dashboard's own
 * corner for the same chat — and there is exactly ONE of it: it is not in the
 * bar as well. Neither adds a control to the board or to a trip's action row;
 * a row of buttons on a phone is the thing this screen exists to avoid.
 *
 * The frame also holds WHAT THE SCREEN IS ABOUT. Each dispatcher screen
 * publishes its own subject through `useAskSubject` — a trip its booking
 * REFERENCE, the board its period — and the launcher seeds the composer with
 * it. The frame cannot read a child route's `:bookingId`, and that id is a
 * mongo ObjectId it would be wrong to show or to send; see DispatcherAssistant
 * for the whole reasoning.
 */
import React from 'react';
import { Outlet } from 'react-router-dom';
import BrandLogo from '../Components/BrandLogo';
import usePortalPrincipal from '../Operator/usePortalPrincipal';
import signOutOfPortal from '../signOutOfPortal';
import {
  DispatcherDeviceButton, DispatcherDeviceNudge, DispatcherDeviceSheet, useDispatcherDevice,
} from './DispatcherDevice';
import {
  DispatcherAssistantLauncher, DispatcherAssistantSheet, useDispatcherAssistant,
} from './DispatcherAssistant';
import { AskSubjectProvider, useAskSubjectState } from './askSubject';
import '../Operator/Operator.scss';
import './Dispatcher.scss';

export default function DispatcherLayout() {
  const { principal } = usePortalPrincipal();
  // The Home Screen offer and notifications, for the phone this is open on.
  // It lives in the frame rather than on the board because the bell has to
  // be reachable from every dispatcher screen, and the state behind the
  // bell, the nudge and the sheet is one state (DispatcherDevice).
  const device = useDispatcherDevice();
  // The chat agent. Its state lives here, not in the sheet, so a dispatcher
  // can close the sheet mid-answer, look at the board, and come back to the
  // finished reply — the dot on the button says when there is one.
  const assistant = useDispatcherAssistant();
  // What the screen inside the frame is about, for the launcher to offer.
  const ask = useAskSubjectState();
  // A dispatcher has no operator and no driver row to be named after, so the
  // login's own address is who they are.
  const who = principal?.email || '';

  return (
    <div className="pp-op pp-dispatch">
      <header className="pp-op__bar">
        <div className="pp-op__bar-left">
          <BrandLogo variant="white" height={34} />
          <span className="pp-dispatch__badge">Dispatch</span>
        </div>
        <div className="pp-op__who">
          {who ? <span className="pp-op__who-name">{who}</span> : null}
          <DispatcherDeviceButton device={device} />
          <span className="pp-op__avatar" aria-hidden="true">PH</span>
          <button type="button" className="pp-op__signout" onClick={signOutOfPortal}>
            Sign out
          </button>
        </div>
      </header>

      <main className="pp-op__main">
        {/* Above the board, never inside it: one line, dismissed for good in
            one tap, and nothing at all on a desk or once it is installed. */}
        <DispatcherDeviceNudge device={device} />
        {/* Every screen under here may say what it is about (useAskSubject). */}
        <AskSubjectProvider setSubject={ask.setSubject}>
          <Outlet />
        </AskSubjectProvider>
      </main>

      {/* Outside <main>, because it floats over it rather than sitting in the
          flow — and last in the DOM, so it is the final tab stop on the screen
          instead of coming between the bar and the board. */}
      <DispatcherAssistantLauncher assistant={assistant} subject={ask.subject} />

      {device.open ? <DispatcherDeviceSheet device={device} /> : null}
      {assistant.open ? <DispatcherAssistantSheet assistant={assistant} /> : null}
    </div>
  );
}
